import type { AuthUser, LoginResponse } from './types';
import { clearAllAskReimagineSessions } from '../utils/askReimagineSessionStore';

let accessToken: string | null = null;
let currentUser: AuthUser | null = null;
let refreshPromise: Promise<string> | null = null;

import { getApiBaseUrl } from './apiBaseUrl';

const API_BASE_URL = getApiBaseUrl();

/**
 * Marks that this browser tab completed a login in the current browsing session.
 * sessionStorage survives page refresh (F5) but is cleared when the tab/window is closed,
 * so a fresh site open requires login again while in-tab reloads keep the session.
 */
const TAB_SESSION_KEY = 'reimagine.auth.tabSession';

type SessionListener = (user: AuthUser | null) => void;
const sessionListeners = new Set<SessionListener>();

export function subscribeToSession(listener: SessionListener): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

function notifySessionListeners(): void {
  sessionListeners.forEach((listener) => listener(currentUser));
}

function hasActiveTabSession(): boolean {
  try {
    return sessionStorage.getItem(TAB_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function markTabSessionActive(): void {
  try {
    sessionStorage.setItem(TAB_SESSION_KEY, '1');
  } catch {
    // Private mode / blocked storage — session restore on refresh will not work.
  }
}

function clearTabSession(): void {
  try {
    sessionStorage.removeItem(TAB_SESSION_KEY);
  } catch {
    // ignore
  }
}

function setSession(token: string, user: AuthUser): void {
  accessToken = token;
  currentUser = user;
  markTabSessionActive();
  notifySessionListeners();
}

function clearSession(): void {
  accessToken = null;
  currentUser = null;
  clearTabSession();
  notifySessionListeners();
  clearAllAskReimagineSessions();
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function getCurrentUser(): AuthUser | null {
  return currentUser;
}

export function isAuthenticated(): boolean {
  return accessToken !== null && currentUser !== null;
}

async function parseErrorMessage(response: Response): Promise<string> {
  try {
    const body = await response.json();
    return body?.message || 'Request failed.';
  } catch {
    return 'Request failed.';
  }
}

/** Clears a leftover HttpOnly refresh cookie without touching in-memory state. */
async function discardStaleRefreshCookie(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } catch {
    // ignore — first visit or network blip
  }
}

export async function login(
  email: string,
  password: string,
  ipAddress?: string,
  userAgent?: string,
  inviteToken?: string
): Promise<AuthUser> {
  // Password is sent in the JSON body; TLS (HTTPS) encrypts it in transit.
  // Hashing is done on the server after this request arrives.
  const response = await fetch(`${API_BASE_URL}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({
      email,
      password,
      ipAddress,
      userAgent,
      ...(inviteToken ? { inviteToken } : {}),
    }),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  const data: LoginResponse = await response.json();
  setSession(data.accessToken, data.user);
  return data.user;
}

export async function register(
  email: string,
  password: string,
  projectId: number,
  isSuperUser = false
): Promise<AuthUser> {
  // Same transport rules as login — HTTPS required in production.
  const response = await fetch(`${API_BASE_URL}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ email, password, projectId, isSuperUser }),
  });

  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }

  const data: LoginResponse = await response.json();
  setSession(data.accessToken, data.user);
  return data.user;
}

/**
 * Restores the session on page refresh using the HttpOnly refresh cookie,
 * but only when this tab already had an authenticated session (sessionStorage flag).
 * A fresh site open has no flag, so the user must log in again.
 */
export async function initializeSession(): Promise<AuthUser | null> {
  if (accessToken && currentUser) {
    return currentUser;
  }

  if (!hasActiveTabSession()) {
    void discardStaleRefreshCookie();
    return null;
  }

  try {
    const token = await refreshAccessToken();
    if (!token) {
      return accessToken && currentUser ? currentUser : null;
    }

    // Login may have completed while refresh was in flight
    if (currentUser) {
      return currentUser;
    }

    const response = await fetch(`${API_BASE_URL}/auth/me`, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    });

    if (!response.ok) {
      if (!accessToken) {
        clearSession();
      }
      return currentUser;
    }

    const data = await response.json();
    currentUser = data.user;
    notifySessionListeners();
    return currentUser;
  } catch {
    // Refresh cookie missing or expired — require login again
    if (!accessToken) {
      clearSession();
    }
    return currentUser;
  }
}

/**
 * Single-flight refresh ensures concurrent 401 responses share one refresh call.
 * Requires an active tab session so a leftover cookie cannot silently re-authenticate
 * after a fresh site open.
 */
export async function refreshAccessToken(): Promise<string> {
  if (!hasActiveTabSession()) {
    throw new Error('Session expired.');
  }

  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async () => {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Session expired.');
    }

    const data = await response.json();
    accessToken = data.accessToken;
    return data.accessToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
}

export async function logout(): Promise<void> {
  try {
    await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
    });
  } finally {
    clearSession();
  }
}

export function clearAuthenticationState(): void {
  clearSession();
}
